import React, { useState, useEffect } from "react";
import { axiosInstance } from "../../utils/commonImports";
import {
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";

import VehicleDropdown from "../../components/VehicleDropdown";
import VendorDropdown from "../../components/VendorDropdown";
import DriverDropdown from "../../components/DriverDropdown";
import CategoryDropdown from "../../components/CategoryDropdown";
import MyDatePicker from "../../components/MyDatePicker";

const initialDuty = {
  trip_duty_id: null,
  duty: "",
  location: "",
  vehicleid: null,
  assigned_vehicle_group_id: "",
  dispatched_vehicle_group_id: "",
  assigned_vehicle_group_name: "",
  dispatched_vehicle_group_name: "",
  is_vendor_tripsheet: false,
  vendorid: null,
  vendor_vehicle_no: "",
  driver_id: null,
  driver_name: "",
  driver_mobile: "",
  open_date: null,
  close_date: null,
  open_time: "00:00",
  reporting_time: "00:00",
  close_time: "",
  open_km_reading: "",
  close_km_reading: "",
  trip_itinerary: "",
  trip_passengers: [],
  trip_itineraries: [],
};

const DUTY_TYPE_OPTIONS = [
  { value: "", label: "Select duty type" },
  { value: "Local", label: "Local" },
  { value: "Outstation", label: "Outstation" },
  { value: "EmployeeTransfer", label: "Employee Transfer" },
  { value: "AirportTransfer", label: "Airport Transfer" },
  { value: "Contract", label: "Monthly Contract" },
  { value: "Package", label: "Package" },
  { value: "OSDayRate", label: "OS Day Rate" },
  { value: "KmSlab", label: "Km Slab" },
];

/* ------------------------------------------------------------------ */
/* Design tokens (same as the booking form)                            */
/* ------------------------------------------------------------------ */
const FIELD_H = 40; // one height for every input, select, picker and dropdown

const TONES = {
  danger: {
    bg: "#fef2f2",
    accent: "#dc2626",
    head: "#7f1d1d",
    line: "#fecaca",
  },
  warning: {
    bg: "#fffbeb",
    accent: "#d97706",
    head: "#78350f",
    line: "#fde68a",
  },
};

const DATE_CONTAINER = { width: "100%", marginBottom: 0 };

const C = {
  primary: "#1d4ed8",
  primaryHover: "#1e40af",
  primarySoft: "#eff6ff",
  surface: "#f8fafc",
  border: "#cbd5e1",
  borderStrong: "#94a3b8",
  text: "#1e293b",
  muted: "#475569",
};

const controlSx = {
  minWidth: 0,
  width: "100%",

  "& .MuiFormControl-root, & .MuiAutocomplete-root": {
    width: "100%",
  },

  "& .MuiInputBase-root:not(.MuiInputBase-multiline), & .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root":
    {
      height: FIELD_H,
      boxSizing: "border-box",
      fontSize: 14,
      fontWeight: 500,
      color: C.text,
      backgroundColor: "#fff",
      borderRadius: "8px",
      paddingTop: 0,
      paddingBottom: 0,
    },

  "& .MuiInputBase-input:not(.MuiInputBase-inputMultiline)": {
    height: "100%",
    boxSizing: "border-box",
    padding: "0 12px",
    fontSize: 16,
    fontWeight: 500,
    color: "#0f172a",
    WebkitTextFillColor: "#0f172a",
  },

  "& .MuiSelect-select.MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxSizing: "border-box",
    padding: "0 32px 0 12px",
    minHeight: 0,
    fontSize: 16,
    fontWeight: 500,
    color: "#0f172a",
  },

  "& .MuiAutocomplete-inputRoot": {
    paddingTop: "0 !important",
    paddingBottom: "0 !important",
    paddingLeft: "0 !important",
    flexWrap: "nowrap",
  },

  "& .MuiPickersInputBase-input, & .MuiPickersOutlinedInput-input": {
    fontSize: "16px !important",
    fontWeight: "500 !important",
    color: "#0f172a !important",
    WebkitTextFillColor: "#0f172a !important",
  },

  "& .MuiAutocomplete-endAdornment": { right: 6 },

  "& .MuiInputBase-multiline": {
    fontSize: 14,
    fontWeight: 500,
    color: C.text,
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "10px 12px",
  },

  "& .MuiInputLabel-root": {
    fontSize: 14,
    fontWeight: 500,
    color: C.muted,
    transform: "translate(13px, 11px) scale(1)",
  },

  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    transform: "translate(13px, -8px) scale(0.85)",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: C.primary,
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: C.border,
  },

  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: C.borderStrong,
  },

  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: C.primary,
    borderWidth: 1,
  },

  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: "0 0 0 3px rgba(29,78,216,0.12)",
  },

  "& .MuiFormHelperText-root": {
    fontSize: 12,
    color: C.muted,
    mt: 0.25,
    ml: 0.5,
  },

  "& .MuiSvgIcon-root": {
    fontSize: 18,
  },
};

const readOnlySx = {
  "& .MuiInputBase-root": { backgroundColor: `${C.surface} !important` },
  "& .MuiInputBase-input": {
    color: `${C.text} !important`,
    WebkitTextFillColor: `${C.text} !important`,
  },
};

const btnBase = {
  textTransform: "none",
  fontSize: 14,
  fontWeight: 500,
  borderRadius: "8px",
  boxShadow: "none",
  minWidth: 0,
  px: 2.25,
  height: 36,
};

const primaryBtn = {
  ...btnBase,
  backgroundColor: C.primary,
  "&:hover": { backgroundColor: C.primaryHover, boxShadow: "none" },
};

const ghostBtn = {
  ...btnBase,
  borderColor: C.borderStrong,
  color: C.text,
  backgroundColor: "#fff",
  "&:hover": { backgroundColor: C.surface, borderColor: C.muted },
};

const thSx = {
  fontSize: 13,
  fontWeight: 700,
  color: "#334155",
  whiteSpace: "nowrap",
  backgroundColor: C.surface,
  borderBottom: `1px solid ${C.border}`,
  px: 1.5,
  py: 1,
};

const tdSx = {
  fontSize: 14,
  fontWeight: 500,
  color: C.text,
  borderBottom: `1px solid ${C.border}`,
  px: 1.5,
  py: 1,
};

/* ------------------------------------------------------------------ */
/* Small presentational helpers                                        */
/* ------------------------------------------------------------------ */
const Card = ({ title, subtitle, action, children }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "12px",
      backgroundColor: "#fff",
      boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2.5,
        py: 1.25,
        minHeight: 56,
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

const Grid = ({ cols = 4, children, sx }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2,
      gridTemplateColumns: {
        xs: "1fr",
        sm: "repeat(2, 1fr)",
        md: `repeat(${cols}, 1fr)`,
      },
      alignItems: "start",
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Column span helper for the responsive grid
const span = (n) => ({ gridColumn: { sm: "span 2", md: `span ${n}` } });

const SubLabel = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 3, mb: 2 }}>
    <Typography
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: C.text,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", backgroundColor: C.border }} />
  </Box>
);

// Wrapper for external dropdown / picker components
const Slot = ({ children, sx }) => (
  <Box sx={{ ...controlSx, ...sx }}>{children}</Box>
);

// Text input / select with consistent styling
const Field = ({ options, sx, InputProps, ...props }) => {
  const locked = !!InputProps?.readOnly || !!props.disabled;
  return (
    <TextField
      size="small"
      fullWidth
      select={!!options}
      InputProps={InputProps}
      sx={{ ...controlSx, ...(locked ? readOnlySx : {}), ...sx }}
      {...props}
    >
      {options &&
        options.map((o) => {
          const opt = typeof o === "string" ? { value: o, label: o } : o;
          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: C.text,
              }}
            >
              {opt.label}
            </MenuItem>
          );
        })}
    </TextField>
  );
};

// Shared header for the two warning dialogs
const DialogHeader = ({ tone, icon, title, subtitle, onClose }) => (
  <DialogTitle
    sx={{
      backgroundColor: tone.bg,
      borderBottom: `1px solid ${tone.line}`,
      px: 3,
      py: 2,
      display: "flex",
      alignItems: "center",
      gap: 1.5,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "50%",
        backgroundColor: tone.accent,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flexGrow: 1 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.text }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: C.muted }}>
        {subtitle}
      </Typography>
    </Box>
    <IconButton size="small" onClick={onClose} sx={{ color: C.muted }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </DialogTitle>
);

const dialogPaper = {
  sx: {
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 12px 40px rgba(15,23,42,0.2)",
  },
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
export default function DutyForm({
  dutyForm,
  handleDutyFormChange,
  vehicles,
  vendors,
  drivers,
  vehicleGroups,
  locations,
  onSaveDuty,
  dutyEditIndex,
  setDutyForm,
  setDutyEditIndex,
  clientId,
  setSnackbar, // <-- for snackbar usage
  selectedDutyType,
  setSelectedDutyType,
  selectedVehicleGroupId,
  setSelectedVehicleGroupId,
  // Tripsheet setting
  dutyEntryType = "SINGLE",
  carryForwardOpeningKm = false,
  checkVehicleDocumentsExpiry = false,
  checkTripsheetsReturned = false,
}) {
  const selectedVehicle = vehicles.find(
    (v) => String(v.vehicle_id) === String(dutyForm.vehicleid),
  );
  const vehicleNumber = selectedVehicle ? selectedVehicle.vehicle_number : "";

  // Validation errors
  const [errors, setErrors] = useState({
    closeDate: "",
    closeKm: "",
    closeTime: "",
  });
  const hasErrors = Object.values(errors).some((msg) => msg);
  const errorMessages = Object.values(errors).filter(Boolean);
  const [previousClosingKm, setPreviousClosingKm] = useState(null);

  const [expiredDocuments, setExpiredDocuments] = useState([]);
  const [expiredDocumentsOpen, setExpiredDocumentsOpen] = useState(false);

  const [previousTripsheets, setPreviousTripsheets] = useState([]);
  const [previousTripsheetDialogOpen, setPreviousTripsheetDialogOpen] =
    useState(false);
  const [checkingPreviousTripsheet, setCheckingPreviousTripsheet] =
    useState(false);
  const tripsheetCheckSeq = React.useRef(0); // guards against overlapping/racing calls

  const loadedCheckKeyRef = React.useRef(null);

  useEffect(() => {
    const vehicleId = dutyForm.vehicleid;
    const driverId = dutyForm.driver_id;

    if (dutyForm.is_vendor_tripsheet) return;
    if (!vehicleId) return;
    if (!checkVehicleDocumentsExpiry && !checkTripsheetsReturned) return;

    // If we're editing a duty that is ITSELF already closed,
    // don't bother checking for other pending tripsheets.
    const isCurrentDutyAlreadyClosed =
      dutyEditIndex !== -1 &&
      dutyForm.close_km_reading &&
      Number(dutyForm.close_km_reading) > 0 &&
      dutyForm.close_time &&
      dutyForm.close_time.trim() !== "";

    if (isCurrentDutyAlreadyClosed) {
      return;
    }

    const key = `${vehicleId}-${driverId || ""}-${checkVehicleDocumentsExpiry}-${checkTripsheetsReturned}-${dutyForm.trip_duty_id || ""}`;
    if (loadedCheckKeyRef.current === key) return;
    loadedCheckKeyRef.current = key;

    if (checkVehicleDocumentsExpiry) {
      checkVehicleDocuments(vehicleId);
    }
    if (checkTripsheetsReturned && driverId) {
      checkPreviousTripsheet(vehicleId, driverId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dutyForm.vehicleid,
    dutyForm.driver_id,
    dutyForm.trip_duty_id,
    dutyForm.close_km_reading,
    dutyForm.close_time,
    dutyForm.is_vendor_tripsheet,
    dutyEditIndex,
    checkVehicleDocumentsExpiry,
    checkTripsheetsReturned,
  ]);

  // Helper: Add one day to ISO date
  const addOneDay = (dateStr) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 1);
    return d.toISOString();
  };

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hrs, mins] = timeStr.split(":").map(Number);
    return hrs * 60 + mins;
  };

  // Validation useEffect with auto close-date adjustment
  useEffect(() => {
    let closeDateErr = "";
    let closeKmErr = "";
    let closeTimeErr = "";

    if (dutyForm.open_date && dutyForm.close_date) {
      const openDate = new Date(dutyForm.open_date);
      let closeDate = new Date(dutyForm.close_date);

      if (closeDate < openDate)
        closeDateErr = "Close date cannot be before open date.";
    }

    if (dutyForm.open_km_reading && dutyForm.close_km_reading) {
      const openKm = Number(dutyForm.open_km_reading);
      const closeKm = Number(dutyForm.close_km_reading);

      if (closeKm < openKm) {
        closeKmErr = "Close KM reading cannot be less than Open KM reading.";
      } else if (previousClosingKm !== null && closeKm <= previousClosingKm) {
        closeKmErr = `Close KM reading must be greater than the previous reading (${previousClosingKm}).`;
      }
    }

    if (
      dutyForm.open_date &&
      dutyForm.close_date &&
      dutyForm.open_time &&
      dutyForm.close_time
    ) {
      const openTimeMinutes = timeToMinutes(dutyForm.open_time);
      const closeTimeMinutes = timeToMinutes(dutyForm.close_time);

      let openDateObj = new Date(dutyForm.open_date);
      let closeDateObj = new Date(dutyForm.close_date);

      // If close_time < open_time → treat as next day
      if (closeTimeMinutes < openTimeMinutes) {
        closeDateObj.setDate(openDateObj.getDate() + 1);
        handleDutyFormChange("close_date", closeDateObj.toISOString());
      }

      // After adjustment, check again if close_time < open_time on same day
      if (
        closeDateObj.toDateString() === openDateObj.toDateString() &&
        closeTimeMinutes < openTimeMinutes
      ) {
        closeTimeErr = "Close time must be after Open time.";
      }
    }

    setErrors({
      closeDate: closeDateErr,
      closeKm: closeKmErr,
      closeTime: closeTimeErr,
    });
  }, [
    dutyForm.open_date,
    dutyForm.close_date,
    dutyForm.open_time,
    dutyForm.close_time,
    dutyForm.open_km_reading,
    dutyForm.close_km_reading,
    previousClosingKm,
    handleDutyFormChange,
  ]);

  const handleDutySave = () => {
    if (hasErrors) {
      const firstError = Object.values(errors).find((msg) => msg);
      setSnackbar({
        open: true,
        message: firstError,
        severity: "error",
      });
      return;
    }

    if (!dutyForm.duty) {
      setSnackbar({
        open: true,
        message: "Duty type is required.",
        severity: "error",
      });
      return;
    }

    if (!dutyForm.is_vendor_tripsheet && !dutyForm.vehicleid) {
      setSnackbar({
        open: true,
        message: "Select a vehicle or mark as vendor vehicle.",
        severity: "error",
      });
      return;
    }

    if (dutyForm.is_vendor_tripsheet) {
      if (!dutyForm.vendorid || !dutyForm.vendor_vehicle_no) {
        setSnackbar({
          open: true,
          message: "Enter vendor and vendor vehicle number.",
          severity: "error",
        });
        return;
      }
    }

    const assignedGroup = vehicleGroups.find(
      (g) => g.categoryid === dutyForm.assigned_vehicle_group_id,
    );

    const dispatchedGroup = vehicleGroups.find(
      (g) => g.categoryid === dutyForm.dispatched_vehicle_group_id,
    );

    const dutyFormWithGroupNames = {
      ...dutyForm,

      // fixed: was `categorynameF` (typo), which always came back undefined
      assigned_vehicle_group_name: assignedGroup
        ? assignedGroup.categoryname
        : null,

      dispatched_vehicle_group_name: dispatchedGroup
        ? dispatchedGroup.categoryname
        : null,
    };

    // ==========================================================
    // SAVE / UPDATE DUTY IN PARENT
    // ==========================================================
    onSaveDuty(dutyFormWithGroupNames);

    // IMPORTANT:
    // Do NOT reset dutyEditIndex here.
    // Parent saveDuty() needs the current index to know
    // whether this is an ADD or UPDATE operation.
  };

  // ============================================================
  // Check previous Tripsheet for same Vehicle + Driver
  // ============================================================
  const checkPreviousTripsheet = async (vehicleId, driverId) => {
    if (!vehicleId || !driverId) {
      return;
    }

    const seq = ++tripsheetCheckSeq.current; // mark this as the latest call

    try {
      setCheckingPreviousTripsheet(true);

      const response = await axiosInstance.get(
        "/api/tripsheets/pending-return",
        {
          params: {
            client_id: clientId,
            vehicle_id: vehicleId,
            driver_id: driverId,
            trip_duty_id: dutyForm.trip_duty_id || "",
          },
        },
      );

      // Ignore stale responses if a newer call has since started
      if (seq !== tripsheetCheckSeq.current) {
        return;
      }

      const pendingList = response?.data?.pending_tripsheets || [];

      const stillOpen = pendingList.filter((ts) => {
        const closeKm = Number(ts.close_km_reading);
        const hasCloseKm =
          ts.close_km_reading !== null &&
          ts.close_km_reading !== "" &&
          closeKm > 0;
        const hasCloseTime = !!ts.close_time && ts.close_time.trim() !== "";
        const isClosed = hasCloseKm && hasCloseTime;
        const isCurrentEdit =
          dutyForm.trip_duty_id &&
          String(ts.trip_duty_id) === String(dutyForm.trip_duty_id);
        return !isClosed && !isCurrentEdit;
      });

      if (stillOpen.length > 0) {
        setPreviousTripsheets(stillOpen);
        setPreviousTripsheetDialogOpen(true);
      } else {
        setPreviousTripsheets([]);
        setPreviousTripsheetDialogOpen(false);
      }
    } catch (error) {
      console.error("❌ Failed to check previous Tripsheet:", error);
    } finally {
      if (seq === tripsheetCheckSeq.current) {
        setCheckingPreviousTripsheet(false);
      }
    }
  };

  // ============================================================
  // Vehicle change
  // ============================================================
  const checkVehicleDocuments = async (vehicleId) => {
    if (!checkVehicleDocumentsExpiry || !vehicleId) {
      setExpiredDocuments([]);
      setExpiredDocumentsOpen(false);
      return;
    }
    try {
      const response = await axiosInstance.get(
        `/api/documents/vehicle/${vehicleId}`,
        { params: { client_id: clientId } },
      );
      const documents = Array.isArray(response?.data) ? response.data : [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expired = documents.filter((doc) => {
        if (!doc.document_expiry_date) return false;
        const expiryDate = new Date(doc.document_expiry_date);
        expiryDate.setHours(0, 0, 0, 0);
        return expiryDate < today;
      });
      setExpiredDocuments(expired);
      if (expired.length > 0) setExpiredDocumentsOpen(true);
    } catch (error) {
      console.error("❌ Failed to check vehicle document expiry:", error);
      setExpiredDocuments([]);
      setExpiredDocumentsOpen(false);
    }
  };

  const onVehicleChange = async (vehicle) => {
    if (!vehicle) {
      handleDutyFormChange("vehicleid", null);
      handleDutyFormChange("assigned_vehicle_group_id", null);
      handleDutyFormChange("dispatched_vehicle_group_id", null);
      handleDutyFormChange("assigned_vehicle_group_name", null);
      handleDutyFormChange("dispatched_vehicle_group_name", null);
      handleDutyFormChange("driver_id", null);
      handleDutyFormChange("driver_name", "");
      handleDutyFormChange("driver_mobile", "");
      handleDutyFormChange("open_km_reading", "");

      setPreviousClosingKm(null);
      return;
    }

    const fullVehicle = vehicles.find(
      (v) => String(v.vehicle_id) === String(vehicle.vehicle_id),
    );

    if (!fullVehicle) return;

    const groupId = fullVehicle.vehicle_group_id || null;

    const groupObj = vehicleGroups.find((g) => g.categoryid === groupId);

    handleDutyFormChange("vehicleid", fullVehicle.vehicle_id);

    handleDutyFormChange("assigned_vehicle_group_id", groupId);

    handleDutyFormChange("dispatched_vehicle_group_id", groupId);

    handleDutyFormChange(
      "assigned_vehicle_group_name",
      groupObj ? groupObj.categoryname : null,
    );

    handleDutyFormChange(
      "dispatched_vehicle_group_name",
      groupObj ? groupObj.categoryname : null,
    );

    handleDutyFormChange("driver_id", fullVehicle.vehicle_driver_id || null);

    handleDutyFormChange("driver_name", fullVehicle.vehicle_driver_name || "");

    handleDutyFormChange(
      "driver_mobile",
      fullVehicle.vehicle_driver_mobile_no || "",
    );

    setSelectedVehicleGroupId(groupId);

    // ==========================================================
    // CARRY FORWARD OPENING KM
    // Only for created / own vehicle
    // ==========================================================
    if (carryForwardOpeningKm && fullVehicle.vehicle_id) {
      try {
        const response = await axiosInstance.get(
          `/api/tripsheets/vehicle/${fullVehicle.vehicle_id}/latest-closing-km`,
          {
            params: {
              client_id: clientId,
            },
          },
        );

        const latestClosingKm = response?.data?.latest_closing_km;

        if (
          latestClosingKm !== null &&
          latestClosingKm !== undefined &&
          latestClosingKm !== ""
        ) {
          const km = Number(latestClosingKm);

          setPreviousClosingKm(km);

          handleDutyFormChange("open_km_reading", String(latestClosingKm));
        } else {
          setPreviousClosingKm(null);
          handleDutyFormChange("open_km_reading", "");
        }
      } catch (error) {
        console.error("❌ Failed to fetch latest closing KM:", error);

        setPreviousClosingKm(null);
      }
    } else {
      setPreviousClosingKm(null);
    }
  };

  const onDriverChange = (driverId) => {
    handleDutyFormChange("driver_id", driverId);
    const driver = drivers.find((d) => d.employeeid === driverId);
    if (driver) {
      const fullName = [
        driver.employeefirstname,
        driver.employeemiddlename || "",
        driver.employeelastname || "",
      ]
        .filter(Boolean)
        .join(" ");
      handleDutyFormChange("driver_name", fullName);
      handleDutyFormChange("driver_mobile", driver.employeemobile || "");
    } else {
      handleDutyFormChange("driver_name", "");
      handleDutyFormChange("driver_mobile", "");
    }
  };

  const onVendorChange = (vendor) => {
    const id = vendor?.vendor_id || null;
    handleDutyFormChange("vendorid", id);
  };

  const daysSince = (dateStr) => {
    if (!dateStr) return null;
    const then = new Date(dateStr);
    then.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffMs = today.getTime() - then.getTime();
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  };

  const resetDutyForm = () => {
    setDutyForm({ ...initialDuty });
    setSelectedDutyType("");
    setSelectedVehicleGroupId(null);
    setDutyEditIndex(-1);
  };

  // ---------------- Render JSX ----------------
  return (
    <Box sx={{ mb: 3 }}>
      <Card
        title={
          dutyEditIndex === -1 ? "Add duty" : `Edit duty #${dutyEditIndex + 1}`
        }
        action={
          <FormControlLabel
            label="Vendor vehicle"
            sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: 13 } }}
            control={
              <Checkbox
                size="small"
                checked={!!dutyForm.is_vendor_tripsheet}
                onChange={(e) =>
                  handleDutyFormChange("is_vendor_tripsheet", e.target.checked)
                }
              />
            }
          />
        }
      >
        {/* ---------- Duty & vehicle ---------- */}
        <Grid
          cols={4}
          sx={{
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(7, minmax(0, 1fr))",
            },
          }}
        >
          <Field
            label="Duty type"
            value={dutyForm.duty || ""}
            onChange={(e) => {
              handleDutyFormChange("duty", e.target.value);
              setSelectedDutyType(e.target.value);
            }}
            options={DUTY_TYPE_OPTIONS}
          />
          <Field
            label="Location"
            value={dutyForm.location || ""}
            onChange={(e) => handleDutyFormChange("location", e.target.value)}
            options={[
              { value: "", label: "Select location" },
              ...locations.map((loc) => ({
                value: loc.categoryid,
                label: loc.categoryname,
              })),
            ]}
          />

          {!dutyForm.is_vendor_tripsheet ? (
            <Slot sx={span(2)}>
              <VehicleDropdown
                clientId={clientId}
                vehicleId={dutyForm.vehicleid}
                onVehicleChange={onVehicleChange}
              />
            </Slot>
          ) : (
            <>
             <Slot
  sx={{
    gridColumn: { xs: "span 1", sm: "span 2" },
    width: "100%",
    minWidth: 0,
  }}
>
  <VendorDropdown
    clientId={clientId}
    vendorId={dutyForm.vendorid}
    onVendorChange={onVendorChange}
    location={dutyForm.location}
  />
</Slot>

<Box
  sx={{
    gridColumn: { xs: "span 1", sm: "span 2" },
    width: "100%",
    minWidth: 0,
  }}
>
  <Field
    label="Vendor vehicle no"
    value={dutyForm.vendor_vehicle_no || ""}
    onChange={(e) =>
      handleDutyFormChange("vendor_vehicle_no", e.target.value)
    }
  />
</Box>
            </>
          )}
        </Grid>

        <Grid cols={4} sx={{ mt: 2 }}>
          <Slot>
            <CategoryDropdown
              clientId={clientId}
              categoryType="VehicleGroup"
              name="assigned_vehicle_group_id"
              label="Assigned vehicle group"
              value={dutyForm.assigned_vehicle_group_id || ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                handleDutyFormChange("assigned_vehicle_group_id", selectedId);
                const selectedGroup = vehicleGroups.find(
                  (g) => g.categoryid === selectedId,
                );
                handleDutyFormChange(
                  "assigned_vehicle_group_name",
                  selectedGroup ? selectedGroup.categoryname : null,
                );
                setSelectedVehicleGroupId(selectedId);
              }}
              options={vehicleGroups}
            />
          </Slot>
          <Slot>
            <CategoryDropdown
              clientId={clientId}
              categoryType="VehicleGroup"
              name="dispatched_vehicle_group_id"
              label="Dispatched vehicle group"
              value={dutyForm.dispatched_vehicle_group_id || ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                handleDutyFormChange("dispatched_vehicle_group_id", selectedId);
                const selectedGroup = vehicleGroups.find(
                  (g) => g.categoryid === selectedId,
                );
                handleDutyFormChange(
                  "dispatched_vehicle_group_name",
                  selectedGroup ? selectedGroup.categoryname : null,
                );
              }}
              options={vehicleGroups}
            />
          </Slot>
          <Slot>
            <DriverDropdown
              clientId={clientId}
              driverId={dutyForm.driver_id}
              onDriverChange={onDriverChange}
            />
          </Slot>
          <Field
            label="Driver mobile"
            value={dutyForm.driver_mobile || ""}
            InputProps={{ readOnly: true }}
          />
        </Grid>

     
        {/* ---------- Opening and Closing in one row ---------- */}
        <Grid
          cols={7}
          sx={{
            mt: 2,
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(7, minmax(0, 1fr))",
            },
          }}
        >
          <Slot>
            <MyDatePicker
              label="Open date"
              containerStyle={DATE_CONTAINER}
              value={dutyForm.open_date ? new Date(dutyForm.open_date) : null}
              onChange={(date) => {
                const isoDate = date ? date.toISOString() : null;
                handleDutyFormChange("open_date", isoDate);

                if (
                  !dutyForm.close_date ||
                  new Date(dutyForm.close_date).toDateString() ===
                    new Date(dutyForm.open_date).toDateString()
                ) {
                  handleDutyFormChange("close_date", isoDate);
                }
              }}
            />
          </Slot>

          <Field
            label="Open time"
            type="time"
            value={dutyForm.open_time || ""}
            onChange={(e) => handleDutyFormChange("open_time", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Field
            label="Report time"
            type="time"
            value={dutyForm.reporting_time || ""}
            onChange={(e) =>
              handleDutyFormChange("reporting_time", e.target.value)
            }
            InputLabelProps={{ shrink: true }}
          />

          <Field
            label="Open KM reading"
            type="number"
            value={dutyForm.open_km_reading || ""}
            onChange={(e) =>
              handleDutyFormChange("open_km_reading", e.target.value)
            }
          />

          <Slot>
            <MyDatePicker
              label="Close date"
              containerStyle={DATE_CONTAINER}
              value={dutyForm.close_date ? new Date(dutyForm.close_date) : null}
              onChange={(date) =>
                handleDutyFormChange(
                  "close_date",
                  date ? date.toISOString() : null,
                )
              }
            />
          </Slot>

          <Field
            label="Close time"
            type="time"
            value={dutyForm.close_time || ""}
            onChange={(e) => handleDutyFormChange("close_time", e.target.value)}
            error={!!errors.closeTime}
            InputLabelProps={{ shrink: true }}
          />

          <Field
            label="Close KM reading"
            type="number"
            value={dutyForm.close_km_reading || ""}
            onChange={(e) =>
              handleDutyFormChange("close_km_reading", e.target.value)
            }
            error={!!errors.closeKm}
          />
        </Grid>

        {errorMessages.length > 0 && (
          <Box
            sx={{
              mt: 2,
              px: 1.75,
              py: 1,
              borderRadius: "8px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#b91c1c",
              fontSize: 13,
            }}
          >
            {errorMessages.map((msg) => (
              <div key={msg}>{msg}</div>
            ))}
          </Box>
        )}

        {/* ---------- Itinerary ---------- */}
        <SubLabel>Trip itinerary</SubLabel>
        <Field
          label="Trip itinerary"
          multiline
          rows={2}
          value={dutyForm.trip_itinerary || ""}
          onChange={(e) =>
            handleDutyFormChange("trip_itinerary", e.target.value)
          }
        />

        {/* ---------- Actions ---------- */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          {dutyEditIndex !== -1 && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={resetDutyForm}
              sx={ghostBtn}
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            variant="contained"
            onClick={handleDutySave}
            disabled={hasErrors}
            sx={primaryBtn}
          >
            {dutyEditIndex !== -1
              ? "Update duty"
              : dutyEntryType === "SINGLE"
                ? "Save duty"
                : "+ Add duty"}
          </Button>
        </Box>
      </Card>

      {/* ==========================================================
          EXPIRED VEHICLE DOCUMENTS
          ========================================================== */}
      <Dialog
        open={expiredDocumentsOpen}
        onClose={() => setExpiredDocumentsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaper}
      >
        <DialogHeader
          tone={TONES.danger}
          icon={<ErrorOutlineIcon fontSize="small" />}
          title="Expired vehicle documents"
          subtitle={`${expiredDocuments.length} document${
            expiredDocuments.length !== 1 ? "s" : ""
          } need attention`}
          onClose={() => setExpiredDocumentsOpen(false)}
        />

        <DialogContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Document</TableCell>
                  <TableCell sx={thSx}>Document no.</TableCell>
                  <TableCell sx={thSx}>Expiry date</TableCell>
                  <TableCell align="right" sx={thSx}>
                    Expired
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expiredDocuments.map((doc) => {
                  const days = daysSince(doc.document_expiry_date);
                  return (
                    <TableRow key={doc.document_id} hover>
                      <TableCell sx={{ ...tdSx, fontWeight: 600 }}>
                        {doc.document_name || "Document"}
                      </TableCell>
                      <TableCell sx={tdSx}>{doc.document_no || "-"}</TableCell>
                      <TableCell sx={tdSx}>
                        {doc.document_expiry_date
                          ? new Date(
                              doc.document_expiry_date,
                            ).toLocaleDateString("en-IN")
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={tdSx}>
                        <Chip
                          size="small"
                          label={
                            days !== null
                              ? `${days} day${days !== 1 ? "s" : ""}`
                              : "-"
                          }
                          sx={{
                            bgcolor: TONES.danger.bg,
                            color: TONES.danger.accent,
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 1.5, borderTop: `1px solid ${C.border}` }}
        >
          <Button
            variant="contained"
            onClick={() => setExpiredDocumentsOpen(false)}
            sx={{
              ...btnBase,
              backgroundColor: TONES.danger.accent,
              "&:hover": { backgroundColor: "#b91c1c", boxShadow: "none" },
            }}
          >
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==========================================================
          PREVIOUS TRIPSHEET NOT SUBMITTED
          ========================================================== */}
      <Dialog
        open={previousTripsheetDialogOpen}
        onClose={() => setPreviousTripsheetDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={dialogPaper}
      >
        <DialogHeader
          tone={TONES.warning}
          icon={<ReportProblemIcon fontSize="small" />}
          title={`Previous tripsheet${
            previousTripsheets.length !== 1 ? "s" : ""
          } not submitted`}
          subtitle={`This vehicle and driver have ${
            previousTripsheets.length
          } unclosed tripsheet${previousTripsheets.length !== 1 ? "s" : ""}`}
          onClose={() => setPreviousTripsheetDialogOpen(false)}
        />

        <DialogContent sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>Tripsheet no</TableCell>
                  <TableCell sx={thSx}>Date</TableCell>
                  <TableCell sx={thSx}>Customer name</TableCell>
                  <TableCell sx={thSx}>User / passenger</TableCell>
                  <TableCell sx={thSx}>Closing KM</TableCell>
                  <TableCell sx={thSx}>Closing time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previousTripsheets.map((ts) => (
                  <TableRow key={ts.trip_duty_id} hover>
                    <TableCell sx={{ ...tdSx, fontWeight: 600 }}>
                      {ts.tripsheet_no || "-"}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      {ts.duty_date
                        ? new Date(ts.duty_date).toLocaleDateString("en-IN")
                        : "-"}
                    </TableCell>
                    <TableCell sx={tdSx}>{ts.customer_name || "-"}</TableCell>
                    <TableCell sx={tdSx}>{ts.passenger_name || "-"}</TableCell>
                    <TableCell sx={tdSx}>
                      {ts.close_km_reading || "-"}
                    </TableCell>
                    <TableCell sx={tdSx}>{ts.close_time || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              px: 3,
              py: 1.5,
              backgroundColor: TONES.warning.bg,
              borderTop: `1px solid ${TONES.warning.line}`,
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 600, color: TONES.warning.head }}
            >
              Please submit or close the previous tripsheet(s) before continuing
              with the new duty.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 1.5, borderTop: `1px solid ${C.border}` }}
        >
          <Button
            variant="contained"
            onClick={() => setPreviousTripsheetDialogOpen(false)}
            sx={{
              ...btnBase,
              backgroundColor: TONES.warning.accent,
              "&:hover": { backgroundColor: "#b45309", boxShadow: "none" },
            }}
          >
            Acknowledge
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
